import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Stack, Grid, Card, CardContent, LinearProgress, Alert, Chip, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Avatar, IconButton, Button,
} from '@mui/material';
import { ArrowBack, Business, ReceiptLong, Payments } from '@mui/icons-material';
import { financeWorkspaceService } from '../../services/financeWorkspaceService';
import { formatIndianNumber } from '../../utils/currency';
import BusinessJourney, { JourneyStage } from '../../components/common/BusinessJourney';

const Stat: React.FC<{ label: string; value: React.ReactNode; color?: string; onClick?: () => void }> = ({ label, value, color, onClick }) => (
  <Card variant="outlined" sx={{ height: '100%', cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { borderColor: 'primary.main' } : {} }} onClick={onClick}>
    <CardContent sx={{ py: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, color }}>{value}</Typography>
    </CardContent>
  </Card>
);

const statusChip = (s: string) => {
  const map: any = { PAID: 'success', SENT: 'info', OVERDUE: 'error', PARTIALLY_PAID: 'warning', DRAFT: 'default', CANCELLED: 'default', CONFIRMED: 'info', DELIVERED: 'success', PENDING: 'warning' };
  return <Chip size="small" label={s?.replace('_', ' ')} color={map[s] || 'default'} />;
};

const Customer360Page: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [d, setD] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    financeWorkspaceService.customer360(Number(id))
      .then(setD).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Box sx={{ p: 3 }}><LinearProgress /></Box>;
  if (error || !d) return <Box sx={{ p: 3 }}><Alert severity="error">{error || 'Not found'}</Alert></Box>;

  const s = d.summary || {};
  // AR lifecycle for this customer, derived from real position.
  const journey: JourneyStage[] = [
    { label: 'Orders', state: s.orders > 0 ? 'done' : 'pending' },
    { label: 'Invoiced', state: s.invoices > 0 ? 'done' : 'pending' },
    { label: 'Receivable', state: s.outstanding > 0 ? 'active' : (s.invoices > 0 ? 'done' : 'pending') },
    { label: 'Overdue', state: s.overdue > 0 ? 'active' : 'pending' },
    { label: 'Collected', state: s.invoices > 0 && s.outstanding <= 0 ? 'done' : 'pending' },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate(-1)}><ArrowBack /></IconButton>
        <Avatar sx={{ bgcolor: 'primary.main' }}><Business /></Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{d.profile.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {d.profile.gstin ? <>GSTIN <b style={{ fontFamily: 'monospace' }}>{d.profile.gstin}</b> · </> : ''}{d.profile.city || ''}{d.profile.state ? `, ${d.profile.state}` : ''}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => navigate('/finance/collections')}>Collections</Button>
      </Stack>

      {/* AR lifecycle */}
      <Card variant="outlined" sx={{ mb: 2 }}><CardContent sx={{ py: 1.5 }}>
        <Typography variant="overline" color="text.secondary">Receivables Lifecycle</Typography>
        <BusinessJourney stages={journey} />
      </CardContent></Card>

      {/* Summary */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid size={{ xs: 6, md: 2.4 }}><Stat label="Orders" value={s.orders ?? 0} /></Grid>
        <Grid size={{ xs: 6, md: 2.4 }}><Stat label="Billed" value={formatIndianNumber(s.billed || 0)} /></Grid>
        <Grid size={{ xs: 6, md: 2.4 }}><Stat label="Outstanding" value={formatIndianNumber(s.outstanding || 0)} color="warning.main" onClick={() => navigate('/finance/collections')} /></Grid>
        <Grid size={{ xs: 6, md: 2.4 }}><Stat label="Overdue" value={formatIndianNumber(s.overdue || 0)} color="error.main" /></Grid>
        <Grid size={{ xs: 6, md: 2.4 }}><Stat label="GST Charged" value={formatIndianNumber(d.gst?.tax || 0)} onClick={() => navigate('/finance/gst-reports')} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Invoices */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}><ReceiptLong fontSize="small" /> Recent Invoices</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead><TableRow sx={{ '& th': { fontWeight: 700 } }}>
                <TableCell>Invoice</TableCell><TableCell>Date</TableCell><TableCell align="right">Amount</TableCell><TableCell align="right">Paid</TableCell><TableCell>Status</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {d.invoices.map((i: any, idx: number) => (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{i.ref}</TableCell>
                    <TableCell>{String(i.date).slice(0, 10)}</TableCell>
                    <TableCell align="right">{formatIndianNumber(i.amount)}</TableCell>
                    <TableCell align="right">{formatIndianNumber(i.paid)}</TableCell>
                    <TableCell>{statusChip(i.status)}</TableCell>
                  </TableRow>
                ))}
                {!d.invoices.length && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>No invoices.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, mt: 2 }}>Recent Orders</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead><TableRow sx={{ '& th': { fontWeight: 700 } }}><TableCell>Order</TableCell><TableCell>Date</TableCell><TableCell align="right">Amount</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
              <TableBody>
                {d.orders.map((o: any, idx: number) => (
                  <TableRow key={idx} hover><TableCell sx={{ fontFamily: 'monospace' }}>{o.ref}</TableCell><TableCell>{String(o.date).slice(0, 10)}</TableCell><TableCell align="right">{formatIndianNumber(o.amount)}</TableCell><TableCell>{statusChip(o.status)}</TableCell></TableRow>
                ))}
                {!d.orders.length && <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>No orders.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Timeline */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Activity Timeline</Typography>
          <Card variant="outlined"><CardContent>
            <Box sx={{ position: 'relative', pl: 2, '&:before': { content: '""', position: 'absolute', left: 5, top: 4, bottom: 4, width: 2, bgcolor: 'divider' } }}>
              {d.timeline.map((t: any, i: number) => (
                <Box key={i} sx={{ position: 'relative', pb: 1.5 }}>
                  <Box sx={{ position: 'absolute', left: -15, top: 4, width: 10, height: 10, borderRadius: '50%', bgcolor: t.kind === 'invoice' ? 'success.main' : 'primary.main' }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{String(t.ts).slice(0, 10)}</Typography>
                  <Typography variant="body2">{t.label}</Typography>
                </Box>
              ))}
              {!d.timeline.length && <Typography variant="body2" color="text.secondary">No activity.</Typography>}
            </Box>
          </CardContent></Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Customer360Page;
